import type { AnalyticsQuery } from "schemas/generated/analyticsRead";

import * as React from "react";

import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import useCurrentAnalyticsTimezone from "@v1/features/analytics/useCurrentAnalyticsTimezone";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import useCurrentAnalyticsCurrencyCode from "@v2/features/analytics/hooks/useCurrentAnalyticsCurrencyCode";
import { generateFakeChartDataRangeResult } from "@v2/features/analytics/utils/fakeData";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { analyticsRouter } from "@v2/trpc/routes/analytics";
import { getRangesFromTimePeriod } from "analytics-utils/analytics";

type BasicAnalyticsReadProps = (
  | {
      type: "withoutUrlSyncQuery";
      queryConfig: AnalyticsQuery;
    }
  | {
      type: "withUrlSyncQuery";
      queryConfig: {
        useChartInterval: boolean;
        queryOverrides?: Partial<AnalyticsQuery>;
      };
    }
) & {
  /**
   * NOTE (Ryan, 2026-01-30, REPL-2334): When true, skips the hasData check and queries
   * across all projects in the workspace. This is needed for experiment queries because
   * the analytics tracking pixel may be installed under a different project than the one
   * where the experiment was created, causing hasData to return false for the experiment's
   * project even though data exists under a sibling project.
   */
  isExperimentQuery?: boolean;
};

/**
 * The hook that communicates with the backend to fetch metrics from ClickHouse.
 *
 * This hook can either be used:
 * 1) within the Analytics dashboard (overview page / deep dive page)
 * 2) outside the Analytics Dashboard (in Experiments, or the editor's RightBar's AnalyticsMenuPane)
 *
 * For 1), use the type = 'withUrlSyncQuery', as we want to use the urlSyncedQuery fields that are in the URL.
 * In this case, we'll use the internalQuery and urlSyncedQuery objects exposed by the AnalyticsContext, and simply
 * combine them to form the final AnalyticsQuery.
 *
 * For 2), use the type = 'withoutUrlSyncQuery', as there won't be any fields in the URL (as we're outside the
 * Analytics dashboard). In this case, provide the full AnalyticsQuery to this hook, and it'll use it to query ClickHouse.
 *
 * We should ONLY use the queryOverrides if we purposely don't want the URL params to be in sync with
 * the fields of type UrlSyncedQuery that we're sending to the backend. The only time we do this is in the
 * AnalyticsHostPagesCombobox, where we want to override the urlPath filters so that only urlPaths containing
 * the searchTerm are returned (check the comments in that component for more details).
 *
 * @author Max 2024-12-15
 */
const useBasicAnalyticsRead = ({
  type,
  queryConfig,
  isExperimentQuery = false,
}: BasicAnalyticsReadProps) => {
  const { query: contextQuery } = useAnalyticsQueryContext();
  const timeZone = useCurrentAnalyticsTimezone();
  const currency = useCurrentAnalyticsCurrencyCode();
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();
  const { shouldBlurFullScreen } = useAnalyticsStatus();

  let query: AnalyticsQuery;

  if (type === "withoutUrlSyncQuery") {
    query = queryConfig;
  } else {
    const { useChartInterval, queryOverrides } = queryConfig;

    const { selectedTimePeriod, chartInterval } = contextQuery;

    // Compute the final ranges (with startDatetime/endDatetime/interval) from the fields
    // in the urlSyncedQuery.
    const rangesFromUrlSyncedQuery = getRangesFromTimePeriod({
      selectedTimePeriod,
      chartInterval: useChartInterval ? chartInterval : null,
      timeZone,
    }).updatedRanges;

    // The final AnalyticsQuery is a simple combination of the internalQuery and the urlSyncedQuery
    query = {
      ...contextQuery,
      ranges: rangesFromUrlSyncedQuery,
      ...queryOverrides,
    };
  }

  const isUrlHostsLoading =
    query.urlHosts !== undefined && query.urlHosts.length === 0;

  const shouldFetch = Boolean(workspaceId || projectId) && !isUrlHostsLoading;

  // NOTE (Ryan, 2026-01-30, REPL-2334): For experiment queries, we skip the hasData check
  // because the data may be stored under a different project in the same workspace (due to
  // the analytics tracking pixel being installed under the first project that connected to
  // Shopify). The find query will use workspace-level projectIds for experiments.
  const hasData = analyticsRouter.hasData.useQuery(
    { workspaceId: workspaceId ?? undefined, projectId, isV2: true },
    { enabled: shouldFetch && !isExperimentQuery },
  );

  const hasAnyData = Boolean(hasData.data);

  const find = analyticsRouter.find.useQuery(
    {
      query: {
        ...query,
        targetCurrency: currency,
      },
      workspaceId: workspaceId ?? undefined,
      projectId,
      isV2: true,
    },
    { enabled: shouldFetch && (isExperimentQuery || hasAnyData) },
  );

  const refetch = async () => Promise.all([find.refetch(), hasData.refetch()]);

  const mainRangeId = query.ranges.mainRange.id;
  const compareAtRangeId = query.ranges.compareAtRanges[0]?.id;

  const rangeResults = find.data?.rangeResults;

  const { mainRangeResults, compareAtRangeResults } = React.useMemo(
    () => ({
      mainRangeResults:
        rangeResults?.filter(
          (rangeResultObj) => rangeResultObj.rangeId === mainRangeId,
        ) ?? [],
      compareAtRangeResults:
        rangeResults?.filter(
          (rangeResultObj) => rangeResultObj.rangeId === compareAtRangeId,
        ) ?? [],
    }),
    [rangeResults, mainRangeId, compareAtRangeId],
  );

  // NOTE (Max, 2025-06-11): If true, then user doesn't have any data, so we generate fake data.
  // In our <AnalyticsDashboard> component, we blur the screen (i.e. this fake data) if shouldBlurFullScreen
  // is true. This allows to tease the user.
  if (shouldBlurFullScreen) {
    const dummyTotalRowsCount = 10;
    const dummyMainRangeResults = Array.from({ length: 10 }, () => {
      return generateFakeChartDataRangeResult();
    });
    const dummyCompareAtRangeResults = Array.from({ length: 10 }, () => {
      return generateFakeChartDataRangeResult();
    });

    return {
      refetch,
      totalRowsCount: dummyTotalRowsCount,
      isLoading: false,
      mainRangeResults: dummyMainRangeResults,
      compareAtRangeResults: dummyCompareAtRangeResults,
      constructedQuery: query,
      loadedButNoDataForWorkspace: false,
      queryHasNoData: false,
    };
  }

  const totalRowsCount = find.data?.totalRowsCount ?? null;

  const loadedButNoDataForWorkspace = !find.isLoading && !hasAnyData;

  const queryHasNoData =
    !find.isLoading &&
    (totalRowsCount === 0 || !totalRowsCount || !mainRangeResults);

  return {
    refetch,
    totalRowsCount,
    isLoading:
      find.isLoading ||
      (!isExperimentQuery && hasData.isLoading) ||
      isUrlHostsLoading,
    mainRangeResults,
    compareAtRangeResults,
    constructedQuery: query,
    loadedButNoDataForWorkspace,
    queryHasNoData,
  };
};

export default useBasicAnalyticsRead;
